// ============================================================================
// SkeletonCard Component - Loading placeholder for cards
// ============================================================================
// Displays animated skeleton loading state for different card types
// ============================================================================

interface SkeletonCardProps {
  variant: 'project' | 'agent' | 'user'
  count?: number
}

export default function SkeletonCard({ variant, count = 1 }: SkeletonCardProps) {
  const cards = Array.from({ length: count }, (_, i) => i)

  return (
    <>
      {cards.map((i) => (
        <div key={i} className="skeleton-card">
          {variant === 'project' && <ProjectSkeleton />}
          {variant === 'agent' && <AgentSkeleton />}
          {variant === 'user' && <UserSkeleton />}
        </div>
      ))}
    </>
  )
}

function ProjectSkeleton() {
  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      {/* Cover image skeleton */}
      <div className="skeleton skeleton-image" style={{ height: '180px', marginBottom: '1rem' }} />

      {/* Title skeleton */}
      <div className="skeleton skeleton-text" style={{ width: '80%', height: '24px', marginBottom: '0.75rem' }} />

      {/* Description skeleton */}
      <div className="skeleton skeleton-text" style={{ width: '100%', height: '16px', marginBottom: '0.5rem' }} />
      <div className="skeleton skeleton-text" style={{ width: '90%', height: '16px', marginBottom: '1rem' }} />

      {/* Stats skeleton */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
        <div className="skeleton skeleton-text" style={{ width: '60px', height: '16px' }} />
        <div className="skeleton skeleton-text" style={{ width: '60px', height: '16px' }} />
        <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px' }} />
      </div>
    </div>
  )
}

function AgentSkeleton() {
  return (
    <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
      {/* Avatar skeleton */}
      <div
        className="skeleton"
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          margin: '0 auto 1rem'
        }}
      />

      {/* Name skeleton */}
      <div className="skeleton skeleton-text" style={{ width: '70%', height: '20px', margin: '0 auto 0.5rem' }} />

      {/* Domain skeleton */}
      <div className="skeleton skeleton-text" style={{ width: '50%', height: '16px', margin: '0 auto 1rem' }} />

      {/* Stats skeleton */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' }}>
        <div className="skeleton skeleton-text" style={{ width: '50px', height: '16px' }} />
        <div className="skeleton skeleton-text" style={{ width: '50px', height: '16px' }} />
      </div>
    </div>
  )
}

function UserSkeleton() {
  return (
    <div className="card" style={{ padding: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
      {/* Avatar skeleton */}
      <div
        className="skeleton"
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          flexShrink: 0
        }}
      />

      <div style={{ flex: 1 }}>
        {/* Name skeleton */}
        <div className="skeleton skeleton-text" style={{ width: '60%', height: '20px', marginBottom: '0.5rem' }} />

        {/* Bio skeleton */}
        <div className="skeleton skeleton-text" style={{ width: '100%', height: '16px', marginBottom: '0.25rem' }} />
        <div className="skeleton skeleton-text" style={{ width: '80%', height: '16px' }} />
      </div>
    </div>
  )
}
